import { Injectable } from '@angular/core';
import { TransfersActions } from '@app/client/account/admin/transfers/actions';
import { decodeRegionFromId } from '@app/shared/helpers/helpers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { errors, GetTransferOutput, ListTransfersOutput, Transfer } from '@smash-sdk/transfer/ejs/01-2024';
import { Region } from '@smash-sdk/core';
import { from, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { Constant } from 'src/constant';


@Injectable()
export class TransfersEffects {

  loadTransfers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.loadTransfers),
      switchMap(() => {
        const transferSdk = new Transfer();
        return from(transferSdk.listTransfers()).pipe(
          map(({ transfers }) => {
            const transfersSorted = [...transfers].sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()) as ListTransfersOutput['transfers'] & { team: string, receiversNumber: number, sender: string, receiverEmails: string, type: string }[];
            transfersSorted.map((transfer => {
              const transferWithMetadata = transfer as ListTransfersOutput['transfers'][0] & { team: string, receiversNumber: number, sender: string, receiverEmails: string, type: string };
              transferWithMetadata.transferDownloads = transfer.transferDownloads + transfer.filesDownloads;
              if (transfer?.delivery?.type) {
                transferWithMetadata.type = transfer.delivery.type;
              }
              if (transfer?.delivery?.sender?.email) {
                transferWithMetadata.sender = transfer.delivery.sender.email;
              }
              if (transfer?.delivery?.receivers) {
                if (transfer.delivery.receivers.length === 1) {
                  transferWithMetadata.receiverEmails = transfer.delivery.receivers[0];
                } else if (transfer.delivery.receivers.length > 1) {
                  transferWithMetadata.receiverEmails = `${transfer.delivery.receivers[0]} (+${transfer.delivery.receivers.length - 1})`;
                }
              }
              transfer.team = /:\/\/([^\/]+)/.exec(transfer.transferUrl)[1].split('.')[0];
              transferWithMetadata.receiversNumber = (transfer?.delivery?.receivers ? transfer.delivery.receivers.length : 0);
            }));

            return TransfersActions.loadTransfersSuccess({ transfers: transfersSorted });
          }),
          catchError(error =>
            of(TransfersActions.loadTransfersFailure({ error: error.errorMessage }))
          )
        );
      })
    )
  );

  loadTransfer = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.loadTransfer),
      map(action => action.transferId),
      switchMap((transferId) => {
        return [
          TransfersActions.loadTransferDetails({ transferId }),
          TransfersActions.loadTransferDetailsFiles({ transferId }),
          TransfersActions.loadTransferEvents({ transferId })
        ];
      }),
    )
  );

  loadTransferDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.loadTransferDetails),
      concatMap((action) => {
        const decodedRegion = decodeRegionFromId(action.transferId);
        const transferSdk = new Transfer({ region: decodedRegion as Region });
        return from(transferSdk.getTransfer({ transferId: action.transferId })).pipe(
          map(({ transfer }) => {
            const transformedTransfer = {
              ...transfer,
              delivery: {
                ...transfer.delivery,
                receivers: transfer?.delivery?.receivers ? transfer.delivery.receivers.map(receiver => ({ email: receiver })) : []
              }
            } as GetTransferOutput['transfer'] & { delivery: { receivers: { email: string }[] } };
            return TransfersActions.loadTransferDetailsSuccess({ transfer: transformedTransfer });
          }),
          catchError(error =>
            of(TransfersActions.loadTransferDetailsFailure({ error: error.errorMessage }))
          )
        );
      }),
    )
  );

  loadTransferEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.loadTransferEvents),
      concatMap((action) => {
        const transferSdk = new Transfer();
        return from(transferSdk.getTransfer({ transferId: action.transferId })).pipe(
          concatMap(({ transfer }) => {
            const isMonoFile = transfer.filesNumber === 1;
            const eventsGroupedByEmail = new Map();
            if (transfer?.delivery?.receivers) {
              const receivers = transfer?.delivery?.receivers;
              receivers.forEach(receiver => {
                eventsGroupedByEmail[receiver] = { email: receiver, transferViews: 0, transferDownloads: 0, filesDownloads: 0, events: [] };
              });
            }
            return from(transferSdk.listTransferEvents({ transferId: action.transferId })).pipe(
              concatMap(({ events }) => {
                // FIX ME - sort by created date, seem to be missing from the API
                const sortedEvents = events.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
                const eventsWithEmail = sortedEvents.filter(event => event.email);

                eventsWithEmail.forEach(event => {
                  if (eventsGroupedByEmail[event.email]) {
                    if (event.type === Constant.transferEventStatus.view) {
                      ++eventsGroupedByEmail[event.email].transferViews;
                    }
                    if (event.type === Constant.transferEventStatus.download && event.resource === Constant.transferEventResource.transfer) {
                      ++eventsGroupedByEmail[event.email].transferDownloads;
                    }
                    if (event.type === Constant.transferEventStatus.download && event.resource === Constant.transferEventResource.file) {
                      // FIXME
                      if (isMonoFile) {
                        ++eventsGroupedByEmail[event.email].transferDownloads;
                      }
                      ++eventsGroupedByEmail[event.email].filesDownloads;
                    }
                    eventsGroupedByEmail[event.email].events.push(event);
                  }
                });

                const eventsGroupedByAnonymousUser = new Map();
                const eventsAnonymous = events.filter(event => !event.email);

                eventsAnonymous.forEach(event => {
                  if (!eventsGroupedByAnonymousUser[event.userId]) {
                    eventsGroupedByAnonymousUser[event.userId] = { userId: event.userId, transferViews: 0, transferDownloads: 0, filesDownloads: 0, events: [] };
                  }
                  if (event.type === Constant.transferEventStatus.view) {
                    ++eventsGroupedByAnonymousUser[event.userId].transferViews;
                  }
                  if (event.type === Constant.transferEventStatus.download && event.resource === Constant.transferEventResource.transfer) {
                    ++eventsGroupedByAnonymousUser[event.userId].transferDownloads;
                  }
                  if (event.type === Constant.transferEventStatus.download && event.resource === Constant.transferEventResource.file) {
                    // FIXME
                    if (isMonoFile) {
                      ++eventsGroupedByAnonymousUser[event.userId].transferDownloads;
                    }
                    ++eventsGroupedByAnonymousUser[event.userId].filesDownloads;
                  }
                  eventsGroupedByAnonymousUser[event.userId].events.push(event);
                });

                const eventsGroupedByEmailArray = Object.keys(eventsGroupedByEmail).map(email => eventsGroupedByEmail[email]);
                const eventsGroupedByAnonymousUserArray = Object.keys(eventsGroupedByAnonymousUser).map((userId) => ({ ...eventsGroupedByAnonymousUser[userId] }));
                return [TransfersActions.loadTransferEventsSuccess({ events, eventsGroupedByEmailArray, eventsGroupedByAnonymousUserArray })];
              }
              ),
              catchError(error => {
                if (error instanceof errors.ListTransferEventsError.NotFoundError) {
                  const eventsGroupedByEmailArray = Object.keys(eventsGroupedByEmail).map(email => eventsGroupedByEmail[email]);
                  return [TransfersActions.loadTransferEventsSuccess({ events: [], eventsGroupedByEmailArray, eventsGroupedByAnonymousUserArray: [] })];
                } else {
                  of(TransfersActions.loadTransferEventsFailure({ error: error.errorMessage }));
                }
              }
              )
            );
          })
        );
      })
    )
  );

  loadTransferDetailsFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.loadTransferDetailsFiles),
      concatMap((action) => {
        const decodedRegion = decodeRegionFromId(action.transferId);
        const transfer = new Transfer({ region: decodedRegion as Region });
        return from(transfer.listTransferFiles({ transferId: action.transferId })).pipe(
          map(({ files }) => {
            return TransfersActions.loadTransferDetailsFilesSuccess({ transferFiles: files });
          }),
          catchError(error =>
            of(TransfersActions.loadTransferDetailsFilesFailure({ error: error.errorMessage }))
          )
        )
      }
      )
    )
  );

  deleteTransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.deleteTransfer),
      concatMap((action) => {
        const decodedRegion = decodeRegionFromId(action.transferId);
        const transferSdk = new Transfer({ region: decodedRegion as Region });
        return from(transferSdk.deleteTransfer({ transferId: action.transferId })).pipe(
          concatMap((transfer: any) => {
            return [
              TransfersActions.deleteTransferSuccess({ transferId: transfer.transfer }),
              TransfersActions.loadTransfers(),
            ];
          }
          ),
          catchError(error =>
            of(TransfersActions.deleteTransferFailure({ error: error.errorMessage }))
          )
        )
      }
      )
    )
  );

  listTransfersReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.listTransfersReceived),
      switchMap(() => {
        const transferSdk = new Transfer();
        return from(transferSdk.listReceipts()).pipe(
          map(({ receipts }) => {
            const receiptsOrdered = receipts.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
            return TransfersActions.listTransfersReceivedSuccess({ transfers: receiptsOrdered });
          }),
          catchError(error =>
            of(TransfersActions.listTransfersReceivedFailure({ error: error.errorMessage }))
          )
        );
      })
    )
  );

  getReceipt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.getReceipt),
      switchMap((action) => {
        const transferSdk = new Transfer();
        return from(transferSdk.getReceipt({ receiptId: action.receiptId })).pipe(
          map(({ receipt }) => {
            return TransfersActions.getReceiptSuccess({ receipt });
          }),
          catchError(error =>
            of(TransfersActions.getReceiptFailure({ error: error.errorMessage }))
          )
        );
      })
    )
  );

  listReceiptFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.listReceiptFiles),
      switchMap((action) => {
        const transferSdk = new Transfer();
        return from(transferSdk.listReceiptFiles({ receiptId: action.receiptId })).pipe(
          map(({ files }) => {
            return TransfersActions.listReceiptFilesSuccess({ files });
          }),
          catchError(error =>
            of(TransfersActions.listReceiptFilesFailure({ error: error.errorMessage }))
          )
        );
      })
    )
  );

  deleteTransferReceived = createEffect(() =>
    this.actions$.pipe(
      ofType(TransfersActions.deleteTransferReceived),
      switchMap((action) => {
        const transferSdk = new Transfer();
        return from(transferSdk.deleteReceipt({ receiptId: action.receiptId })).pipe(
          map(({ receipt }) => {
            return TransfersActions.deleteTransferReceivedSuccess({ receipt });
          }),
          catchError(error =>
            of(TransfersActions.deleteTransferReceivedFailure({ error: error.errorMessage }))
          )
        );
      })
    )
  );

  constructor(private actions$: Actions,
  ) { }

}